import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  inject,
  DestroyRef,
} from '@angular/core';
import { FieldType } from '../../ag1-table.component';
import { Ag1TableService } from '../../ag1-table.service';
import { take, map, switchMap } from 'rxjs/operators';
import { Popover } from 'primeng/popover';
import { Ag1ListSearchComponent } from '@app/core/components/ag1-list-search/ag1-list-search.component';
import { Bind } from 'primeng/bind';
import { Button } from 'primeng/button';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { Ag1ListSearchComponent as Ag1ListSearchComponent_1 } from '../../../ag1-list-search/ag1-list-search.component';
import { FormsModule } from '@angular/forms';
import { Tooltip } from 'primeng/tooltip';
import { HumanizePipe } from '@app/core/pipes/humanize.pipe';
import { SentenceCasePipe } from '../../../../pipes/sentence.pipe';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'ag1-table-column-grouping',
  templateUrl: './ag1-table-column-grouping.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    Bind,
    Button,
    AngularSvgIconModule,
    Popover,
    Ag1ListSearchComponent_1,
    FormsModule,
    Tooltip,
    HumanizePipe,
    SentenceCasePipe,
  ],
})
export class Ag1TableColumnGroupingComponent implements OnInit {
  private _destroyRef = inject(DestroyRef);
  private cdr = inject(ChangeDetectorRef);

  fields: FieldType[];
  filteredFields: FieldType[];

  isLoading: boolean;
  isActive: boolean = false;

  @ViewChild('groupPopover')
  groupPopover!: Popover;
  @ViewChild(Ag1ListSearchComponent) searchComponent: Ag1ListSearchComponent;

  /** (Required) Service that contains the state of the Ag1TableBase components */
  @Input() tableService: Ag1TableService;

  // column grouping
  /** (Required) Event triggered when grouping a column. The purpose of this is to allow a feature to trigger data fetching */
  @Output() columnGroupEvent = new EventEmitter<void>();

  constructor() {}

  ngOnInit(): void {
    if (!this.tableService) {
      console.error('Table Service is required');
      return;
    }

    this.tableService.isLoading$
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        switchMap((isLoading) => {
          this.isLoading = isLoading;

          // start listening to fields$ observable when isLoading$ emits a value
          return this.tableService.fields$.pipe(
            takeUntilDestroyed(this._destroyRef),
            map((fields) => {
              this.fields = fields
                .filter((f) => f.groupable)
                .sort((a, b) => {
                  if (a.groupOrder === null) return 1;
                  if (b.groupOrder === null) return -1;
                  return a.groupOrder - b.groupOrder;
                })
                .map((f, index) => {
                  return { ...f, groupOrder: index };
                });

              this.filteredFields = [...this.fields];
              this.refreshInterface();
            }),
          );
        }),
      )
      .subscribe();
  }

  refreshInterface(): void {
    this.cdr.detectChanges();
  }

  updateColumnGroupOrder(field: FieldType, groupOrder: number): void {
    this.fields = this.fields
      .map((f) => {
        if (f.name === field.name) {
          return { ...f, groupOrder };
        }

        return f;
      })
      .sort((a, b) => {
        if (a.groupOrder === null) return 1;
        if (b.groupOrder === null) return -1;
        return a.groupOrder - b.groupOrder;
      })
      .map((f, index) => {
        return { ...f, groupOrder: index };
      });

    this.filteredFields = [...this.fields];
  }

  updateColumnGrouping(field: FieldType, grouped: boolean): void {
    this.fields = this.fields.map((f) => {
      if (f.name === field.name) {
        return { ...f, grouped };
      }

      return f;
    });

    this.filteredFields = [...this.fields];
  }

  applyColumnGrouping(): void {
    const groupedFields: FieldType[] = this.fields.filter(
      // may need to relax this condition in the future
      (f) => f.grouped && f.groupable && f.sortable,
    );

    this.tableService.fields$
      .pipe(
        take(1),
        map((fields: FieldType[]) => {
          const updatedFields: FieldType[] = fields.map((f: FieldType) => {
            const groupedField = groupedFields.find((gf) => gf.name === f.name);
            if (groupedField) {
              return {
                ...f,
                groupOrder: groupedField.groupOrder,
                grouped: true,
                sortDirection: f.sortDirection || 'asc',
                checked: false, // hide grouped fields by default
              };
            }

            return {
              ...f,
              groupOrder: null,
              grouped: false,
              sortDirection: null,
            };
          });

          // console.log('grouped fields', updatedFields);
          return updatedFields;
        }),
      )
      .subscribe((fields) => {
        this.closeGroupPanel();
        this.tableService.updateFields(fields);
        this.columnGroupEvent.emit();
      });
  }

  clearColumnGrouping(): void {
    this.tableService.fields$
      .pipe(
        take(1),
        map((fields: FieldType[]) => {
          const updatedFields: FieldType[] = fields.map((f: FieldType) => {
            return { ...f, grouped: false, sortDirection: null };
          });

          // console.log('grouped fields', updatedFields);
          return updatedFields;
        }),
      )
      .subscribe((fields) => {
        this.closeGroupPanel();
        this.tableService.updateFields(fields);
      });
  }

  closeGroupPanel(): void {
    this.groupPopover.hide();
  }

  resetFilteredFields(): void {
    this.filteredFields = [...this.fields];
  }

  onMenuOpen(): void {
    this.isActive = true;
    setTimeout(() => {
      this.searchComponent?.focusSearch();
    });
  }

  onMenuClose(): void {
    this.isActive = false;
    this.resetFilteredFields();
  }
}
